/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AxiosInstance } from "axios";
import axios from "axios";

import type { ApiRequestOptions } from "./ApiRequestOptions";
import type { ApiResult } from "./ApiResult";
import { CancelablePromise } from "./CancelablePromise";
import type { OpenAPIConfig } from "./OpenAPI";
import { classifyError } from "./request-error-interceptor";
import { reportError } from "@/shared/ui/components/basic/error-notification/report-error";

import { isSuccess } from "./request-guards";
import { getUrl } from "./request-query";
import { getFormData } from "./request-form-data";
import { getHeaders, getRequestBody } from "./request-headers";
import { sendRequest } from "./request-errors";
import { getResponseHeader, getResponseBody, catchErrorCodes } from "./request-response";

// Re-export the decomposed building blocks so existing imports from this module
// (and the generated services) keep working unchanged.
export { isDefined, isString, isStringWithValue, isBlob, isFormData, isSuccess, base64 } from "./request-guards";
export { getQueryString } from "./request-query";
export { getFormData } from "./request-form-data";
export { resolve, getHeaders, getRequestBody } from "./request-headers";
export { sendRequest } from "./request-errors";
export { __setDevSimulatorForTest } from "./request-dev-simulator";
export { getResponseHeader, getResponseBody, catchErrorCodes } from "./request-response";

/**
 * Detects the partnerships-search crawl failure: a 502 originating from the
 * `/partnerships/search` endpoint, which is treated as expected (not reported).
 *
 * @param error - The thrown error to inspect
 * @param options - The request options identifying the endpoint
 * @returns True when the error is a partnerships-search 502 crawl failure
 */
function isPartnershipsCrawlFailure(error: unknown, options: ApiRequestOptions): boolean {
  return (
    options.url.includes('/partnerships/search') &&
    error instanceof Error && 'status' in error && (error as any).status === 502
  );
}

/**
 * Decides whether an error should be reported to the monitoring pipeline: only
 * critical, non-GET failures that are not the expected partnerships crawl 502.
 *
 * @param error - The thrown error to classify
 * @param options - The request options (method and endpoint)
 * @returns True when the error warrants reporting
 */
function shouldReportError(error: unknown, options: ApiRequestOptions): boolean {
  if (classifyError(error) !== 'critical') return false;
  if (options.method === 'GET') return false;
  return !isPartnershipsCrawlFailure(error, options);
}

/**
 * Handles a failed request by logging anti-bot unavailability and forwarding
 * reportable errors to the monitoring pipeline.
 *
 * @param error - The thrown error to handle
 * @param options - The request options (method and endpoint)
 */
function handleRequestError(error: unknown, options: ApiRequestOptions): void {
  if (error instanceof Error && error.name === 'AntiBotUnavailableError') {
    console.error('[ANTIBOT] Request aborted — could not generate anti-bot token', {
      method: options.method,
      url: options.url,
      error_message: error.message,
    });
  }
  if (shouldReportError(error, options)) {
    reportError(error);
  }
}

/**
 * Request method
 * @param config The OpenAPI configuration object
 * @param options The request options from the service
 * @param axiosClient The axios client instance to use
 * @returns CancelablePromise<T>
 * @throws ApiError
 */
export const request = <T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  axiosClient: AxiosInstance = axios
): CancelablePromise<T> => {
  return new CancelablePromise(async (resolve, reject, onCancel) => {
    try {
      const url = getUrl(config, options);
      const formData = getFormData(options);
      const body = getRequestBody(options);
      const headers = await getHeaders(config, options, formData);

      if (!onCancel.isCancelled) {
        const response = await sendRequest<T>(config, options, url, body, formData, headers, onCancel, axiosClient);
        const responseBody = getResponseBody(response);
        const responseHeader = getResponseHeader(response, options.responseHeader);

        const result: ApiResult = {
          url,
          ok: isSuccess(response.status),
          status: response.status,
          statusText: response.statusText,
          body: responseHeader ?? responseBody,
        };

        catchErrorCodes(options, result);
        resolve(result.body);
      }
    } catch (error) {
      handleRequestError(error, options);
      reject(error);
    }
  });
};
